import { Calendar, Filter, LayoutGrid, List, Maximize, Minimize, Table } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getStatusColor } from '../constants';

interface CalendarHeaderProps {
  monthRangeText: string;
  postsCount: number;
  isExpanded: boolean;
  statusFilter: string | null;
  tip?: string;
  onFullscreenClick: () => void;
  onStatusFilterChange: (status: string | null) => void;
}

const statuses = [
  {key: 'draft', label: 'Draft'},
  {key: 'approved', label: 'Approved'},
  {key: 'scheduled', label: 'Scheduled'},
  {key: 'posted', label: 'Posted'},
] as const;

export function CalendarHeader({
                                 monthRangeText,
                                 postsCount,
                                 isExpanded,
                                 statusFilter,
                                 tip,
                                 onFullscreenClick,
                                 onStatusFilterChange,
                               }: CalendarHeaderProps) {
  return (
    <TooltipProvider>
      <div className={`sticky top-0 z-10 bg-card relative flex items-center justify-between border-b p-2`}>
        {/* Left side: Title (only in expanded) or Views (only in non-expanded) */}
        <div className={`${isExpanded ? 'hidden sm:flex' : 'flex'} items-center gap-2`}>
          {isExpanded ? (
            <h2 className="text-xl font-bold text-foreground">{monthRangeText}</h2>
          ) : (
            <TabsList>
              <TabsTrigger value="calendar" className="gap-2">
                <Calendar className="h-4 w-4"/>
                <span className="hidden sm:inline">Calendar</span>
              </TabsTrigger>
              <TabsTrigger value="list" className="gap-2">
                <List className="h-4 w-4"/>
                <span className="hidden sm:inline">List</span>
              </TabsTrigger>
            </TabsList>
          )}
        </div>

        {/* Center: Views (only in expanded) */}
        {isExpanded && (
          <div className="sm:absolute sm:left-1/2 sm:-translate-x-1/2">
            <TabsList>
              <TabsTrigger value="calendar" className="gap-2">
                <Calendar className="h-4 w-4"/>
                <span className="hidden sm:inline">Calendar</span>
              </TabsTrigger>
              <TabsTrigger value="list" className="gap-2">
                <List className="h-4 w-4"/>
                <span className="hidden sm:inline">List</span>
              </TabsTrigger>
              {/*<TabsTrigger value="calendar+list" className="gap-2">*/}
              {/*  <LayoutGrid className="h-4 w-4"/>*/}
              {/*  <span className="hidden sm:inline">Combined</span>*/}
              {/*</TabsTrigger>*/}
              <TabsTrigger value="table" className="gap-2">
                <Table className="h-4 w-4"/>
                <span className="hidden sm:inline">Table</span>
              </TabsTrigger>
            </TabsList>
          </div>
        )}

        {/* Right side: Post count + Status filter (expanded only) + Refresh + Fullscreen button */}
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground font-medium flex-shrink-0">
            {postsCount} {postsCount === 1 ? 'post' : 'posts'}
          </div>

          {/* Status filter select - only in expanded mode */}
          {isExpanded && (
            <Select
              value={statusFilter || 'all'}
              onValueChange={(value) => onStatusFilterChange(value === 'all' ? null : value)}
            >
              <SelectTrigger className="w-auto h-9 gap-2 flex-shrink-0">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4"/>
                    {statusFilter ? (
                      <>
                        <div className={`w-2 h-2 rounded-full ${getStatusColor(statusFilter)}`}/>
                        <span>{statuses.find(s => s.key === statusFilter)?.label}</span>
                      </>
                    ) : (
                      <span>All</span>
                    )}
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <span>All Posts</span>
                  </div>
                </SelectItem>
                {statuses.map((status) => (
                  <SelectItem key={status.key} value={status.key}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(status.key)}`}/>
                      <span>{status.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Fullscreen button - always visible */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onFullscreenClick}
                className="flex-shrink-0"
              >
                {isExpanded ? (
                  <Minimize className="h-4 w-4"/>
                ) : (
                  <Maximize className="h-4 w-4"/>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isExpanded ? 'Exit fullscreen' : 'Enter fullscreen'}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}
